// src/pages/eventos/ConfigurarCombos.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase/firebaseConfig';
import { doc, updateDoc } from 'firebase/firestore';
import { getDoc } from '../../utils/firestoreRead';

export default function ConfigurarCombos() {
  const { eventoId } = useParams();
  const navigate = useNavigate();

  const [evento, setEvento] = useState(null);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const ref = doc(db, 'eventos', eventoId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setMensaje('Evento no encontrado');
          setLoading(false);
          return;
        }
        const data = snap.data();
        setEvento({ id: snap.id, ...data });

        const base = Array.isArray(data.combos) ? data.combos : [];
        setCombos(
          base.map((c, idx) => ({
            id: c.id || `combo-${idx}`,
            nombre: c.nombre || '',
            descripcion: c.descripcion || '',
            precioCentavos: Number(c.precioCentavos || 0),
            cantidadEntradas: Number(c.cantidadEntradas || 1),
          }))
        );
      } catch (e) {
        console.error('Error cargando evento:', e);
        setMensaje('Error al cargar el evento');
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, [eventoId]);

  const agregarCombo = () => {
    setCombos((prev) => [
      ...prev,
      {
        id: `combo-${Date.now()}`,
        nombre: 'Entrada individual',
        descripcion: '',
        precioCentavos: 0,
        cantidadEntradas: 1,
      },
    ]);
  };

  const actualizarCampoCombo = (index, campo, valor) => {
    setCombos((prev) =>
      prev.map((c, i) =>
        i === index
          ? {
              ...c,
              [campo]:
                campo === 'precioCentavos' || campo === 'cantidadEntradas'
                  ? Number(valor || 0)
                  : valor,
            }
          : c
      )
    );
  };

  const eliminarComboIndex = (index) => {
    setCombos((prev) => prev.filter((_, i) => i !== index));
  };

  const guardarCombos = async () => {
    if (!evento) return;
    setSaving(true);
    try {
      if (combos.some((c) => !Number.isSafeInteger(c.precioCentavos) || c.precioCentavos < 0 || c.precioCentavos > 100000000 || !Number.isSafeInteger(c.cantidadEntradas) || c.cantidadEntradas < 1)) {
        throw new Error('Los precios requieren centavos enteros no negativos y las cantidades deben ser enteras positivas.');
      }
      const combosLimpios = combos
        .filter((c) => c.nombre.trim() && c.cantidadEntradas > 0)
        .map((c, idx) => ({
          id: c.id || `combo-${idx}`,
          nombre: c.nombre.trim(),
          descripcion: (c.descripcion || '').trim(),
          precioCentavos: Number(c.precioCentavos || 0),
          cantidadEntradas: Number(c.cantidadEntradas || 1),
        }));

      await updateDoc(doc(db, 'eventos', evento.id), {
        combos: combosLimpios,
      });

      setMensaje('Combos guardados correctamente');
    } catch (e) {
      console.error('Error guardando combos:', e);
      setMensaje('No se pudo guardar la configuración de combos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pm-config-combos"
        style={{
          minHeight: '100vh', backgroundColor: 'var(--pm-bg)', color: 'var(--pm-text)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Cargando...
      </div>
    );
  }

  if (!evento) {
    return (
      <div className="pm-config-combos"
        style={{
          minHeight: '100vh', backgroundColor: 'var(--pm-bg)', color: 'var(--pm-text)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div>{mensaje || 'Evento no encontrado'}</div>
        <button
          type="button"
          onClick={() => navigate('/eventos')}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: 'none',
            background: 'var(--pm-surface-raised)',
            color: 'var(--pm-text)',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Volver a eventos
        </button>
      </div>
    );
  }

  return (
    <div className="pm-config-combos"
      style={{
        minHeight: '100vh', backgroundColor: 'var(--pm-bg)', color: 'var(--pm-text)',
        padding: 20,
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: 700 }}>
        <button
          type="button"
          onClick={() => navigate('/eventos')}
          style={{
            marginBottom: 12,
            padding: '6px 12px',
            borderRadius: 999,
            border: 'none',
            background: 'var(--pm-border)',
            color: 'var(--pm-text)',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          ← Volver a eventos
        </button>

        <h2
          style={{
            textAlign: 'center',
            marginBottom: 12,
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          Configurar combos
        </h2>

        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 12,
            background: 'var(--pm-input)',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}
        >
          {(evento.arteInvitacion || evento.arteEntrada) && (
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 8,
                overflow: 'hidden',
                background: 'var(--pm-border)',
                flexShrink: 0,
              }}
            >
              <img
                src={(evento.arteInvitacion || evento.arteEntrada)}
                alt={evento.nombre}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {evento.nombre}
            </div>
            <div style={{ fontSize: 13, color: 'var(--pm-muted)' }}>
              {evento.fechaTexto}
            </div>
          </div>
        </div>

        {combos.length === 0 && (
          <p style={{ fontSize: 14, color: 'var(--pm-muted)', marginBottom: 10 }}>
            Aún no hay combos configurados para este evento. Agrega al menos uno
            para usarlo en &quot;Generar Entradas&quot;.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginBottom: 12,
          }}
        >
          {combos.map((combo, index) => (
            <div
              key={combo.id || index}
              style={{
                borderRadius: 10,
                border: '1px solid var(--pm-border)',
                background: 'var(--pm-surface)',
                padding: 10,
              }}
            >
              <div style={{ marginBottom: 6 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Nombre del combo
                </label>
                <input
                  value={combo.nombre}
                  onChange={(e) =>
                    actualizarCampoCombo(index, 'nombre', e.target.value)
                  }
                  placeholder="Ej: Entrada individual, Paquete x10"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid var(--pm-border)',
                    fontSize: 13,
                  }}
                />
              </div>

              <div style={{ marginBottom: 6 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    marginBottom: 4,
                  }}
                >
                  Descripción (opcional)
                </label>
                <input
                  value={combo.descripcion}
                  onChange={(e) =>
                    actualizarCampoCombo(
                      index,
                      'descripcion',
                      e.target.value
                    )
                  }
                  placeholder="Ej: Mesa + 1 botella"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid var(--pm-border)',
                    fontSize: 13,
                  }}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 13,
                      marginBottom: 4,
                    }}
                  >
                    Entradas
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={combo.cantidadEntradas}
                    onChange={(e) =>
                      actualizarCampoCombo(
                        index,
                        'cantidadEntradas',
                        e.target.value
                      )
                    }
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 8,
                      border: '1px solid var(--pm-border)',
                      fontSize: 13,
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 13,
                      marginBottom: 4,
                    }}
                  >
                    Precio total (Bs)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={combo.precioCentavos / 100}
                    onChange={(e) =>
                      actualizarCampoCombo(index, 'precioCentavos', Math.round(Number(e.target.value) * 100))
                    }
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 8,
                      border: '1px solid var(--pm-border)',
                      fontSize: 13,
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 12,
                }}
              >
                <div style={{ color: 'var(--pm-muted)' }}>
                  {combo.precioCentavos > 0 && combo.cantidadEntradas > 0 && (
                    <>
                      ≈ Bs{' '}
                      {(combo.precioCentavos / 100 / combo.cantidadEntradas).toFixed(1)} por entrada
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => eliminarComboIndex(index)}
                  style={{
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#fee2e2',
                    color: '#b91c1c',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 10,
          }}
        >
          <button
            type="button"
            onClick={agregarCombo}
            style={{
              padding: '8px 12px',
              borderRadius: 999,
              border: 'none',
              background: '#020617',
              color: 'var(--pm-text)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            + Agregar combo
          </button>
          <button
            type="button"
            onClick={guardarCombos}
            disabled={saving}
            style={{
              padding: '8px 12px',
              borderRadius: 999,
              border: 'none',
              background: 'var(--pm-accent)',
              color: 'var(--pm-on-accent)',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {saving ? 'Guardando...' : 'Guardar combos'}
          </button>
        </div>

        {mensaje && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              padding: 8,
              borderRadius: 8,
              background: mensaje.includes('Error') ? '#fee2e2' : '#dcfce7',
              color: mensaje.includes('Error') ? '#991b1b' : '#166534',
            }}
          >
            {mensaje}
          </div>
        )}
      </div>
    </div>
  );
}
